
    <template>
      <component :is="tag">
        <h1 id="front-matter-and-body">Front Matter and Body</h1>
<p>Nuxtent processes markdown files in two main parts: the data contained in the file’s <code>front-matter</code> and all the other data contained after it. The front-matter data will be destructured, while the body of the file will be appropriately available under the <code>body</code> property.</p>
<p>The front matter must follow three rules:</p>
<ol>
<li>It must take the form of valid <code>yaml</code></li>
<li>It must be the first thing in the file</li>
<li>It must contained between a set of triple-dashed lines.</li>
</ol>
<p>Here is a basic example:</p>
<pre><code class="language-md">---
title: Nuxtent
---

...is awesome!

</code></pre>
<p>Will result in the following data:</p>
<pre><code class="language-json">{
  title: 'Nuxtent',
  body: '&lt;p&gt;...is awesome!&lt;/p&gt;'
}
</code></pre>
<h3 id="reserved-attributes">Reserved attributes</h3>
<p>Since the markdown file’s front-matter properties are destructured with all other top level properties, you have to be careful not to use the following reserved properties:</p>
<p>Here is a list of reserved attributes:</p>
<ul>
<li><code>meta</code>, contains meta information about the file, such as the <code>fileName</code> and <code>section</code>.</li>
<li><code>date</code>, contains the file’s date if it is a post, otherwise it defaults to the date the file was last updated.</li>
<li><code>path</code>, contains the file’s entire url path, including any nested routes.</li>
<li><code>permalink</code>, contains the file’s path paramters.</li>
<li><code>body</code>, contains the content below the <code>front-matter</code>.</li>
<li><code>anchors</code>, contains the page’s heading that was converted to link anchors for page navigation.</li>
</ul>
<h1 id="async-components">Async Components</h1>
<p>Nuxtent allows you to use <code>Vue Component</code> inside your markdown files.</p>
<p>In order to do this, you must prefix the file name with <code>.comp.md</code> so that the file can be detected by the appropriate loader.</p>
<p>Usage Syntax: <code>@[pathToComponent](componentProps)</code></p>
<p>As you can tell, the syntax is similar to <code>links</code>, except it is prefixed by an <code>at sign</code>. This which helps <code>Nuxtent</code> detect a component so that it can dynamically import it.</p>
<p><em>Note: all imports are relative to <code>~components</code> directory</em></p>
<p>For example, this markdown component:</p>
<pre><code class="language-md">@[example/project](name=&quot;Hello World&quot;)
</code></pre>
<p>Is converted to:</p>
<pre><code class="language-html">&lt;ExampleProject name=&quot;Hello World&quot; /&gt;
</code></pre>
<p>And imported and registered as:</p>
<pre><code class="language-js">import ExampleProject from '~components/project.vue'
export default {
  components: {
    ExampleProject
  }
}
</code></pre>
<p>Now it’s simple to spice up your content with demos and other sorts of components.</p>
<Logo  />
<p><em>Note: Markdown components require additional processing and should only be used when necessary in order to keep build and load times to a minimum.</em></p>

      </component>
    </template>

    <script>
    import { interopDefault } from '~/.nuxt/utils'
    export default {
        name: 'Writing',
        components: {
          Logo: () => import('~/components/Logo.vue')
        },
        data: () => ({"title":"Writing Your Content","order":4}),
        props: {
          tag: {
            default: 'div',
            type: [String, Object]
          }
        }
      }
    </script>
  